import {
    Grid,
    IOption,
    SelectChangeEvent,
    useAppDispatch,
    useAppSelector,
    useTheme,
} from '../../base';
import {
    StyledDashboardPageWrapper,
    StyledSkeleton,
} from './DashboardPage.styles';
import { useState, useCallback } from 'react';
import { dashboardActions, dashboardSelectors } from '../../actions';
import {
    TopBarComponent,
    StatsCard,
    TasksCard,
    DashboardAlerts,
    JobsTableCard,
    SourcesCard,
} from '../../components/DashboardComponents';
import { DASHBOARD_TIME_RANGES } from '../../common';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';

const DashboardPage = () => {
    const { t } = useTranslation();
    const [timeRange, setTimeRange] = useState<IOption>({
        value: DASHBOARD_TIME_RANGES.MONTH,
        label: t('dashboardPage.month'),
    });
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const topMetricStats = useAppSelector(dashboardSelectors.getStats);
    useEffectOnce(() => {
        dispatch(dashboardActions.fetchNotifications());
        dispatch(dashboardActions.fetchAssignments());
        dispatch(dashboardActions.fetchTrackableStatistics());
    });

    const fetchDashboardData = useCallback(() => {
        dispatch(dashboardActions.fetchStats());
    }, [dispatch]);

    useEffectOnce(() => {
        fetchDashboardData();
    });

    const handleTimeRangeChange = (event: SelectChangeEvent<unknown>) => {
        const value = event.target.value;
        let label = '';
        switch (value) {
            case DASHBOARD_TIME_RANGES.WEEK:
                label = t('dashboardPage.week');
                break;
            case DASHBOARD_TIME_RANGES.MONTH:
                label = t('dashboardPage.month');
                break;
            case DASHBOARD_TIME_RANGES.QUARTER:
                label = t('dashboardPage.quarter');
                break;
            case DASHBOARD_TIME_RANGES.YEAR:
                label = t('dashboardPage.year');
                break;
        }
        setTimeRange({
            value: event.target.value as string,
            label: label,
        });
    };

    const renderTopMetricsSection = () => {
        if (topMetricStats && Object.keys(topMetricStats).length > 0) {
            return topMetricStats[timeRange?.value as string].map(
                (topMetric, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <StatsCard
                            statsData={topMetric}
                            timePeriod={timeRange}
                        />
                    </Grid>
                )
            );
        } else {
            return Array(4)
                .fill(0)
                .map((_, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <StyledSkeleton variant="rectangular" height={120} />
                    </Grid>
                ));
        }
    };

    return (
        <StyledDashboardPageWrapper>
            <Grid container spacing={2.5}>
                <Grid item xs={12}>
                    <TopBarComponent
                        timeRange={timeRange}
                        onTimeRangeChange={handleTimeRangeChange}
                        onRefresh={fetchDashboardData}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Grid
                        container
                        spacing={3}
                        sx={{ paddingBottom: theme.spacing(2) }}
                    >
                        {renderTopMetricsSection()}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} md={6}>
                            <TasksCard />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Grid container spacing={2.5}>
                                <Grid item xs={12}>
                                    <DashboardAlerts />
                                </Grid>
                                <Grid item xs={12}>
                                    <SourcesCard timeRange={timeRange.value} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <JobsTableCard />
                </Grid>
            </Grid>
        </StyledDashboardPageWrapper>
    );
};

export default DashboardPage;
